// 공용 버튼.
// 디자인 토큰 기반 단일 버튼. 화면마다 반복되던 "rounded bg-... px-... py-..." 문자열을 이 컴포넌트로 대체한다.
// 변형(variant)/크기(size)/로딩/전체폭을 지원하고, 최소 터치 영역(44px+)을 기본 보장한다.

import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "./cn.ts";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  loading?: boolean;
  children: ReactNode;
}

// 변형별 색상: primary=브랜드 채움, secondary=경계선, ghost=배경 없음, danger=위험(파괴적 동작 전용).
const variantClass: Record<Variant, string> = {
  primary: "bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800",
  secondary:
    "border border-ink-300 bg-surface text-ink-900 hover:bg-ink-50 active:bg-ink-100",
  ghost: "text-ink-700 hover:bg-ink-100 active:bg-ink-200",
  danger: "bg-danger-600 text-white hover:bg-danger-700",
};

// 크기별 높이/패딩/글자. md 가 기본이며 모두 최소 터치 영역을 만족하도록 높이를 잡는다.
const sizeClass: Record<Size, string> = {
  sm: "min-h-touch px-3 text-sm",
  md: "min-h-[3rem] px-4 text-base", // 48px
  lg: "min-h-[3.5rem] px-5 text-lg", // 56px - 결제 등 핵심 CTA
};

// 토큰 기반 공용 버튼. 변형/크기/로딩/전체폭과 최소 터치 영역(44px+)을 한 곳에서 보장한다.
export default function Button({
  variant = "primary",
  size = "md",
  fullWidth = false,
  loading = false,
  disabled,
  className,
  children,
  type = "button",
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading;
  return (
    <button
      // 로딩 중에는 type 무관하게 비활성 처리(중복 제출 방지)
      type={type}
      disabled={isDisabled}
      // 로딩 상태를 보조기기에 알림
      aria-busy={loading || undefined}
      className={cn(
        "inline-flex select-none items-center justify-center gap-2 rounded font-semibold",
        "transition-colors duration-fast ease-standard",
        "disabled:cursor-not-allowed disabled:opacity-50",
        variantClass[variant],
        sizeClass[size],
        fullWidth && "w-full",
        className,
      )}
      {...rest}
    >
      {/* 로딩 스피너: 텍스트 옆에 작은 회전 인디케이터 */}
      {loading ? (
        <span
          aria-hidden="true"
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      ) : null}
      {children}
    </button>
  );
}
